import { isApiError, unwrap } from "@sagot/api-client";
import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, Icon, StatusBadge, toast } from "@sagot/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../../session/workspace-context";
import { wsKeys, type IntegrationOut } from "../../shared";
import { savePendingConnect } from "../google-callback/pending-connect";
import { isGoogleAuthorizeUrl } from "../use-google-calendar";
import { Gate, useCanManage } from "./panel-kit";

export const GOOGLE_SHEETS_KEY = "google_sheets";
const P = "settings.integrations.sheets";
const SERVICE_UNAVAILABLE = 503;
const GOOGLE_DOCS_PREFIX = "https://docs.google.com/";

/** What the card needs from the integration row (`config` is set by the backend connect flow). */
export function sheetsStateOf(integration: IntegrationOut) {
  const text = (key: string): string | null => {
    const value = integration.config[key];
    return typeof value === "string" && value !== "" ? value : null;
  };
  const url = text("spreadsheet_url");
  return {
    available: integration.available,
    connected: integration.status === "connected",
    title: text("spreadsheet_title"),
    // Only ever link to Google's own spreadsheet host.
    url: url?.startsWith(GOOGLE_DOCS_PREFIX) ? url : null,
  };
}

function useSheetsMutations() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: wsKeys.integrations(wsId) });
  const connect = useMutation({ mutationFn: () => unwrap(api.POST("/v1/integrations/google_sheets/connect")) });
  const disconnect = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/integrations/{key}", { params: { path: { key: GOOGLE_SHEETS_KEY } } })),
    onSuccess: refresh,
  });
  return { connect, disconnect };
}

function DisconnectDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { disconnect } = useSheetsMutations();
  const confirm = () => {
    disconnect.mutate(undefined, {
      onSuccess: () => {
        toast.info(t(`${P}.disconnected`));
        onOpenChange(false);
      },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t(`${P}.disconnectTitle`)}
        description={t(`${P}.disconnectBody`, { product: PRODUCT_NAME })}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={() => { onOpenChange(false); }}>{t("common.cancel")}</Button>
            <Button variant="danger" loading={disconnect.isPending} onClick={confirm}>
              <Icon name="x-circle" />
              {t(`${P}.disconnectConfirm`)}
            </Button>
          </>
        }
      />
    </Dialog>
  );
}

interface GoogleSheetsCardProps {
  integration: IntegrationOut;
  /** Same-tab redirect (rule 8: no auth popups). Injected for tests. */
  redirect?: (url: string) => void;
}

/** Google Sheets card: unavailable → Connect (Google creates the leads spreadsheet) → connected (open, disconnect). */
export function GoogleSheetsCard({ integration, redirect = (url) => { window.location.assign(url); } }: GoogleSheetsCardProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const manage = useCanManage("integrations.manage");
  const state = sheetsStateOf(integration);
  const { connect } = useSheetsMutations();
  const [disconnecting, setDisconnecting] = useState(false);
  const start = () => {
    connect.mutate(undefined, {
      onSuccess: ({ authorize_url }) => {
        if (!isGoogleAuthorizeUrl(authorize_url)) {
          toast.error(t(`${P}.notConfigured`));
          return;
        }
        savePendingConnect({ wsSlug: summary.slug, wsId: summary.id, integration: GOOGLE_SHEETS_KEY });
        redirect(authorize_url);
      },
      onError: (error) => { toast.error(isApiError(error) && error.status === SERVICE_UNAVAILABLE ? t(`${P}.notConfigured`) : message(error)); },
    });
  };
  const redirecting = connect.isPending || connect.isSuccess;
  const reason = !state.connected && !state.available ? t(`${P}.unavailable`) : manage.reason;
  return (
    <article className="card card-body" data-testid="integration-google_sheets" aria-labelledby="gsheets-title">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-sunken text-fg-muted">
          <Icon name="table" className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id="gsheets-title" className="font-bold">{t(`${P}.name`)}</h3>
            {state.connected ? <StatusBadge status="success" label={t("settings.integrations.statusConnected")} /> : null}
            {!state.connected && state.available ? <Badge>{t("settings.integrations.statusDisconnected")}</Badge> : null}
            {!state.connected && !state.available ? <Badge><Icon name="clock" className="size-3" />{t("settings.integrations.statusUnavailable")}</Badge> : null}
          </div>
          <p className="mt-0.5 text-xs text-fg-muted">
            {state.connected ? t(`${P}.connectedTo`, { title: state.title ?? t(`${P}.name`) }) : t(`${P}.description`)}
          </p>
          {state.available || state.connected ? null : <p className="mt-2 text-xs font-semibold text-fg-muted">{t(`${P}.unavailable`)}</p>}
        </div>
        {state.connected ? (
          <Gate reason={reason}>
            <Button variant="secondary" size="sm" disabled={!manage.allowed} onClick={() => { setDisconnecting(true); }}>
              {t("settings.integrations.disconnect")}
            </Button>
          </Gate>
        ) : null}
      </div>
      {state.connected && state.url ? (
        <div className="mt-3 pl-13">
          <Button asChild variant="secondary" size="sm">
            <a href={state.url} target="_blank" rel="noopener noreferrer">
              <Icon name="external-link" />
              {t(`${P}.open`)}
            </a>
          </Button>
        </div>
      ) : null}
      {state.connected ? null : (
        <div className="mt-3 pl-13">
          <Gate reason={reason}>
            <Button size="sm" disabled={!manage.allowed || !state.available} loading={redirecting} onClick={start}>
              <Icon name="log-in" />
              {redirecting ? t(`${P}.redirecting`) : t(`${P}.connect`)}
            </Button>
          </Gate>
        </div>
      )}
      {!state.connected && state.available ? <p className="mt-2 pl-13 text-xs text-fg-subtle">{t(`${P}.connectNote`, { product: PRODUCT_NAME })}</p> : null}
      <DisconnectDialog open={disconnecting} onOpenChange={setDisconnecting} />
    </article>
  );
}
